import { useField, useFormikContext } from 'formik';
import { useEffect, useId, useRef, type RefObject } from 'react';
import { BLOCK_KIND_LABELS, CREATABLE_BLOCK_KINDS, type BlockKind } from '@/blocks/domain/types';
import { RwCombobox, RwField } from '@/common/presentation/components/rw';

export interface NewBlockFormValues {
  kind: BlockKind;
  name: string;
}

const KIND_OPTIONS: BlockKind[] = [...CREATABLE_BLOCK_KINDS];

/**
 * Kind picker. `RwCombobox` is MUI Autocomplete (searchable). Options are the library offer
 * (`CREATABLE_BLOCK_KINDS`), not the full `BlockKind` union. The field is required (it defaults to
 * Letterhead and cannot be cleared), so it carries the asterisk and `aria-required` that match the
 * Yup rule; MUI's asterisk is `aria-hidden`, so the accessible name stays "Kind".
 */
export function KindField({ inputRef }: { inputRef: RefObject<HTMLInputElement | null> }) {
  const [field, meta, helpers] = useField<BlockKind>('kind');
  const hasError = Boolean(meta.touched && meta.error);

  return (
    <RwCombobox
      options={KIND_OPTIONS}
      value={field.value}
      disableClearable
      // All eight kinds show without scrolling: the stock cap (40vh) cut the last row on a laptop-height window.
      slotProps={{ listbox: { sx: { maxHeight: 'min(70vh, 24rem)' } } }}
      onChange={(_event, next) => {
        void helpers.setValue(next);
      }}
      onBlur={() => {
        void helpers.setTouched(true);
      }}
      getOptionLabel={(kind) => BLOCK_KIND_LABELS[kind]}
      isOptionEqualToValue={(option, value) => option === value}
      sx={{ mb: 'var(--size-16)' }}
      renderInput={(params) => (
        <RwField
          {...params}
          name={field.name}
          label="Kind"
          required
          inputRef={inputRef}
          size="small"
          error={hasError}
          helperText={hasError ? meta.error : undefined}
          inputProps={{
            ...params.inputProps,
            'aria-required': true,
            'data-testid': 'document-builder:new-block-dialog:kind'
          }}
        />
      )}
    />
  );
}

/**
 * Required display name (LLAMS-617: a blank name shows "Name is required" and the dialog stays open).
 * After a failed submit focus moves to this field, so the error is read where the user types.
 */
export function NameField() {
  const [field, meta] = useField<string>('name');
  const { submitCount } = useFormikContext<NewBlockFormValues>();
  const nameRef = useRef<HTMLInputElement>(null);
  const helperId = useId();
  const hasError = Boolean(meta.touched && meta.error);

  useEffect(() => {
    if (submitCount > 0 && meta.error) nameRef.current?.focus();
  }, [submitCount, meta.error]);

  return (
    <RwField
      {...field}
      label="Name"
      fullWidth
      size="small"
      inputRef={nameRef}
      error={hasError}
      helperText={hasError ? meta.error : undefined}
      inputProps={{
        maxLength: 200,
        'aria-required': true,
        'aria-invalid': hasError || undefined,
        'aria-describedby': hasError ? helperId : undefined
      }}
      slotProps={{ formHelperText: { id: helperId } }}
    />
  );
}
